// Per-browser settings (localStorage).
import { normalizeTextDensity } from './ui/scale';
export interface Settings {
  appearance: number;
  uiScale: number;        // 0 = automatic
  textSize: number;       // an index into TEXT_SIZES (ui/scale.ts): 1 = normal
  textDensity: number;    // text-only supersampling: 1, 2, 4 (default), or 8
  shadows: boolean;      // all world shadows
  shadowDirection: number; // degrees clockwise: 0 right, 90 down
  shadowDistance: number;  // cast length as a percentage of sprite height
  /** Shadows follow the sun through the day (web/src/game/sun.ts); off, the two above pin them (admin tuning). */
  shadowsFollowSun: boolean;
  entityShadows: boolean; // scenery shadows shade entities behind their caster
  zoomOffset: number;
  masterVolume: number;
  musicVolume: number;
  effectsVolume: number;
  muteWhenUnfocused: boolean;
  profanityFilter: boolean;
  chatTextSize: number;   // an index into CHAT_TEXT_SIZES: 1 = the UI's text size
  chatBubbles: boolean;   // speech bubbles over players who speak
  chatBackground: number; // an index into CHAT_BACKGROUNDS: the band behind chat lines over the world
  questTracker: boolean;  // the active quests on the HUD (toggled from the quest journal)
  showDebug: boolean;
  world: string;          // the chosen world's id (config.json `worlds`); '' = the first
  adminCollapsed: string[]; // admin panel (F5) sections folded shut
}

/** Chat text (and speech bubble) sizes, times the UI's text size. */
export const CHAT_TEXT_SIZES: readonly { name: string; factor: number }[] = [
  { name: 'Small', factor: 0.85 }, { name: 'Normal', factor: 1 }, { name: 'Large', factor: 1.2 }, { name: 'Larger', factor: 1.4 }];
/** How dark the band behind chat lines is while the chat is closed. */
export const CHAT_BACKGROUNDS: readonly { name: string; alpha: number }[] = [
  { name: 'None', alpha: 0 }, { name: 'Light', alpha: 0.5 }, { name: 'Dark', alpha: 0.75 }];

const defaults: Settings = {
  appearance: 0, uiScale: 0, textSize: 1, textDensity: 4, zoomOffset: 0, shadows: true, shadowDirection: 265, shadowDistance: 90, shadowsFollowSun: true, entityShadows: true,
  masterVolume: 0.8, musicVolume: 0.5, effectsVolume: 0.8, muteWhenUnfocused: true, profanityFilter: true,
  chatTextSize: 1, chatBubbles: true, chatBackground: 1, questTracker: true, showDebug: false, world: '', adminCollapsed: [],
};

export const settings: Settings = (() => {
  try {
    const saved = { ...defaults, ...JSON.parse(localStorage.getItem('ashenheart:settings') ?? '{}') };
    return { ...saved, textDensity: normalizeTextDensity(saved.textDensity),
      shadows: typeof saved.shadows === 'boolean' ? saved.shadows : defaults.shadows,
      shadowDirection: Number.isFinite(saved.shadowDirection) ? Math.max(0, Math.min(360, saved.shadowDirection)) : defaults.shadowDirection,
      shadowDistance: Number.isFinite(saved.shadowDistance) ? Math.max(0, Math.min(300, saved.shadowDistance)) : defaults.shadowDistance,
      shadowsFollowSun: typeof saved.shadowsFollowSun === 'boolean' ? saved.shadowsFollowSun : defaults.shadowsFollowSun,
      entityShadows: typeof saved.entityShadows === 'boolean' ? saved.entityShadows : defaults.entityShadows,
      world: typeof saved.world === 'string' ? saved.world : defaults.world,
      chatTextSize: CHAT_TEXT_SIZES[saved.chatTextSize] ? saved.chatTextSize : defaults.chatTextSize,
      chatBubbles: typeof saved.chatBubbles === 'boolean' ? saved.chatBubbles : defaults.chatBubbles,
      chatBackground: CHAT_BACKGROUNDS[saved.chatBackground] ? saved.chatBackground : defaults.chatBackground,
      questTracker: typeof saved.questTracker === 'boolean' ? saved.questTracker : defaults.questTracker,
      adminCollapsed: Array.isArray(saved.adminCollapsed) ? saved.adminCollapsed.filter((x: unknown) => typeof x === 'string').slice(0, 16) : [] };
  } catch { return { ...defaults }; }
})();

export function saveSettings(): void {
  try { localStorage.setItem('ashenheart:settings', JSON.stringify(settings)); } catch { /* storage blocked */ }
}
